<script setup lang="ts">
import { computed } from "vue";
import type { CountdownStyleProps } from "./shared";
import { resolveLayoutTokens, speedDuration } from "./shared";

const props = defineProps<CountdownStyleProps>();

const layout = computed(() => resolveLayoutTokens(props.block, props.viewport));

const tickMs = computed(() => speedDuration(props.block.animationSpeed, 600));
const flickerMs = computed(() =>
  speedDuration(props.block.animationSpeed, 4500),
);

const containerStyle = computed(
  () =>
    ({
      backgroundColor: props.block.backgroundColor || "#000000",
      fontFamily:
        props.block.fontFamily ||
        "'JetBrains Mono', 'Fira Code', 'Courier New', monospace",
      padding: `${layout.value.padding}px`,
      gap: `${layout.value.gap}px`,
      flexDirection: layout.value.stack ? "column" : "row",
      minHeight: layout.value.fullscreen ? "60vh" : undefined,
      "--cd-tick": `${tickMs.value}ms`,
      "--cd-flicker": `${flickerMs.value}ms`,
    }) as Record<string, string | number | undefined>,
);

const digitFontSize = computed(
  () => `${props.block.digitFontSize * layout.value.digitScale}px`,
);

const matrixColumns = Array.from({ length: 24 }, (_, i) => i);
const matrixChars = "ｱｲｳｴｵｶｷｸｹｺｻｼｽｾｿﾀﾁﾂﾃﾄ01010101";

function randomDelay(seed: number): string {
  return `${(seed * 137) % 1800}ms`;
}
function randomChar(seed: number): string {
  return matrixChars[(seed * 7) % matrixChars.length];
}
</script>

<template>
  <div class="cd-matrix" :style="containerStyle">
    <div class="cd-matrix__rain" aria-hidden="true">
      <span
        v-for="col in matrixColumns"
        :key="col"
        class="cd-matrix__col"
        :style="{
          left: `${(col / matrixColumns.length) * 100}%`,
          animationDelay: randomDelay(col),
        }"
      >
        <span v-for="row in 12" :key="row">{{ randomChar(col + row) }}</span>
      </span>
    </div>

    <div class="cd-matrix__scan" aria-hidden="true" />

    <div
      v-for="(seg, i) in segments"
      :key="seg.key"
      class="cd-matrix__cell"
      :style="{
        animationDelay: `${i * 120}ms`,
      }"
    >
      <div
        class="cd-matrix__digit"
        :style="{
          fontSize: digitFontSize,
          color: block.digitColor || '#39ff14',
        }"
      >
        <span :key="seg.value" class="cd-matrix__num">{{ seg.value }}</span>
      </div>
      <div
        class="cd-matrix__label"
        :style="{
          fontSize: `${block.labelFontSize}px`,
          color: block.labelColor || '#39ff14aa',
        }"
      >
        {{ seg.label }}
      </div>
    </div>
  </div>
</template>

<style scoped>
.cd-matrix {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  border-radius: 12px;
  isolation: isolate;
}
.cd-matrix__rain {
  position: absolute;
  inset: 0;
  pointer-events: none;
  opacity: 0.25;
  z-index: 0;
}
.cd-matrix__col {
  position: absolute;
  top: -100%;
  display: flex;
  flex-direction: column;
  font-size: 14px;
  color: #39ff14;
  text-shadow: 0 0 6px #39ff14;
  animation: cd-matrix-rain 3.6s linear infinite;
  font-family: monospace;
  line-height: 1;
}
@keyframes cd-matrix-rain {
  0% {
    transform: translateY(-50%);
    opacity: 0;
  }
  20% {
    opacity: 0.9;
  }
  100% {
    transform: translateY(220%);
    opacity: 0;
  }
}
.cd-matrix__scan {
  position: absolute;
  inset: 0;
  background: repeating-linear-gradient(
    180deg,
    rgba(57, 255, 20, 0) 0,
    rgba(57, 255, 20, 0) 3px,
    rgba(57, 255, 20, 0.07) 4px
  );
  mix-blend-mode: screen;
  pointer-events: none;
  z-index: 1;
}
.cd-matrix__cell {
  position: relative;
  z-index: 2;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 12px 18px;
  border: 1px solid rgba(57, 255, 20, 0.4);
  border-radius: 6px;
  background: rgba(0, 0, 0, 0.55);
  box-shadow:
    0 0 12px rgba(57, 255, 20, 0.35),
    inset 0 0 12px rgba(57, 255, 20, 0.08);
  animation: cd-matrix-flicker var(--cd-flicker, 4500ms) infinite;
}
@keyframes cd-matrix-flicker {
  0%,
  92%,
  100% {
    opacity: 1;
  }
  93%,
  95%,
  97% {
    opacity: 0.7;
  }
  94%,
  96% {
    opacity: 0.95;
  }
}
.cd-matrix__digit {
  font-weight: 700;
  letter-spacing: 0.05em;
  text-shadow:
    0 0 8px currentColor,
    0 0 18px currentColor;
  display: inline-block;
  min-width: 2ch;
  text-align: center;
}
.cd-matrix__num {
  display: inline-block;
  animation: cd-matrix-pop var(--cd-tick, 600ms) ease-out;
}
@keyframes cd-matrix-pop {
  0% {
    transform: translateY(-30%) scale(0.9);
    opacity: 0;
    filter: blur(4px);
  }
  100% {
    transform: translateY(0) scale(1);
    opacity: 1;
    filter: blur(0);
  }
}
.cd-matrix__label {
  margin-top: 6px;
  text-transform: uppercase;
  letter-spacing: 0.25em;
  font-weight: 500;
}
</style>
